import { useMutation, useQuery } from '@apollo/client';
import { NotificationsActive } from '@mui/icons-material';
import { Checkbox, FormControlLabel, Grid, Icon, Slider, Switch } from '@mui/material';

import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import UPDATE_MESSAGING_SETTINGS_MUTATION from 'gql/mutations/updateMessagingSettingsForUser.gql';
import DEPARTMENTS_QUERY from 'gql/queries/getDepartments.gql';
import LOCKED_DEPARTMENTS_QUERY from 'gql/queries/getLockedDepartmentsForEntitlement.gql';
import MESSAGING_SETTINGS_QUERY from 'gql/queries/getMessagingSettingsForUser.gql';

import Subheading from 'app/components/DetailPage/components/Subheading';
import GQLGuard from 'app/components/GQLGuard';
import InfoBox from 'app/components/InfoBox';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Department, LockedDepartments, UserSettings } from 'types/AppTypes';

interface Props {
  user: any;
}

export function MessagingSettings({ user }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [departmentsInitialized, setDepartmentsInitialized] = useState(false);
  const [selectedDepartmentsNotifications, setSelectedDepartmentsNotifications] = useState<
    string[]
  >([]);
  const [selectedDepartmentsEmails, setSelectedDepartmentsEmails] = useState<string[]>([]);
  const [settingsId, setSettingsId] = useState<number>();
  const [emailsEnabled, setEmailsEnabled] = useState<boolean>();

  const departmentsQuery = useQuery<any, any>(DEPARTMENTS_QUERY, {
    variables: { notifications: true }
  });
  const messagingSettingsQuery = useQuery<any, any>(MESSAGING_SETTINGS_QUERY, {
    variables: { userId: user.uid }
  });
  const lockedDepartmentsQuery = useQuery<any, any>(LOCKED_DEPARTMENTS_QUERY, {
    variables: { entitlement: user.role }
  });
  const [updateMessagingSettings] = useMutation(UPDATE_MESSAGING_SETTINGS_MUTATION);

  const handleClick = (type: 'notifications' | 'emails', id: string) => {
    let saveValue;
    if (type === 'notifications') {
      if (selectedDepartmentsNotifications.includes(id)) {
        saveValue = selectedDepartmentsNotifications.filter(d => d !== id);
        setSelectedDepartmentsNotifications(saveValue);
      } else {
        saveValue = [...selectedDepartmentsNotifications, id];
        setSelectedDepartmentsNotifications(saveValue);
      }
      handleSave({
        notificationCategories: saveValue.map(v => parseInt(v, 10))
      });
    }

    if (type === 'emails') {
      if (selectedDepartmentsEmails.includes(id)) {
        saveValue = selectedDepartmentsEmails.filter(d => d !== id);
        setSelectedDepartmentsEmails(saveValue);
      } else {
        saveValue = [...selectedDepartmentsEmails, id];
        setSelectedDepartmentsEmails(saveValue);
      }
      handleSave({
        emailCategories: saveValue.map(v => parseInt(v, 10))
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSave = useCallback(
    debounce(async saveObject => {
      updateMessagingSettings({
        variables: {
          id: settingsId?.toString(),
          settings: {
            ...saveObject
          }
        }
      }).catch(e => {
        dispatch(AlertActions.error(`Error saving messaging settings: ${JSON.stringify(e)}`));
      });
    }, 200),
    [settingsId]
  );

  const sliderMarks = [
    {
      value: 1,
      label: t(translations.pages.profile.days.oneDay())
    },
    {
      value: 7,
      label: t(translations.pages.profile.days.sevenDays())
    },
    {
      value: 14,
      label: t(translations.pages.profile.days.fourteenDays())
    }
  ];

  return (
    <GQLGuard query={[departmentsQuery, lockedDepartmentsQuery, messagingSettingsQuery]}>
      {([
        { departments },
        { lockedDepartmentsForEntitlement },
        { getMessagingSettingsForUser: settings }
      ]: [
        { departments: Department[] },
        { lockedDepartmentsForEntitlement: LockedDepartments },
        {
          getMessagingSettingsForUser: UserSettings;
        }
      ]) => {
        if (!departmentsInitialized) {
          setDepartmentsInitialized(true);
          setSelectedDepartmentsNotifications(settings.notificationCategories.map(c => c.id));
          setSelectedDepartmentsEmails(settings.emailCategories.map(c => c.id));
        }
        if (!settingsId) {
          setSettingsId(settings.id);
          setEmailsEnabled(settings.active);
        }
        return (
          <>
            <Subheading content={t(translations.pages.profile.notificationsSubheader())} />
            <Grid container className="shibUserDetail__info" style={{ marginTop: '-2em' }}>
              <InfoBox
                type="warning"
                title={t(translations.pages.profile.autosaveInfo.title())}
                collapsed
                expandable
              >
                {t(translations.pages.profile.autosaveInfo.content())}
              </InfoBox>
            </Grid>
            <Grid container className="shibUserDetail__categories">
              <Grid container>
                <Grid item xs={3} />
                <Grid item xs={2} className="shibUserDetail__categories__row__checkbox">
                  <Grid container className="shibUserDetail__categories__heading">
                    <Grid item className="shibUserDetail__categories__heading__icon">
                      <NotificationsActive fontSize="medium" />
                    </Grid>
                    <Grid item className="shibUserDetail__categories__heading__title">
                      {t(translations.pages.profile.notificationSettingsHeaders.bell())}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={2} className="shibUserDetail__categories__row__checkbox">
                  <Grid container className="shibUserDetail__categories__heading">
                    <Grid item className="shibUserDetail__categories__heading__icon2">
                      <Icon
                        className="fas fa-envelope"
                        fontSize="small"
                        style={{ justifyContent: 'flex-end' }}
                      />
                    </Grid>
                    <Grid item className="shibUserDetail__categories__heading__title">
                      {t(translations.pages.profile.notificationSettingsHeaders.email())}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {departments.map(d => {
                const isLocked =
                  lockedDepartmentsForEntitlement.departments.findIndex(ld => ld.id === d.id) !==
                  -1;
                return (
                  <Grid container className="shibUserDetail__categories__row">
                    <Grid item xs={3} className="shibUserDetail__categories__row__label">
                      {d.title}
                    </Grid>
                    <Grid item xs={2} className="shibUserDetail__categories__row__checkbox">
                      <Checkbox
                        color="primary"
                        disabled={isLocked}
                        checked={isLocked || selectedDepartmentsNotifications.includes(d.id)}
                        onClick={() => handleClick('notifications', d.id)}
                      />
                    </Grid>
                    <Grid item xs={2} className="shibUserDetail__categories__row__checkbox">
                      <Checkbox
                        color="primary"
                        disabled={isLocked}
                        checked={isLocked || selectedDepartmentsEmails.includes(d.id)}
                        onClick={() => handleClick('emails', d.id)}
                      />
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
            <Subheading content={t(translations.pages.profile.summarySettings())} />
            <Grid container spacing={2} className="shibUserDetail__summary__info">
              <Grid item xs={12} md>
                <InfoBox type="info" title={t(translations.pages.profile.summaryInfo.title())}>
                  {t(translations.pages.profile.summaryInfo.content())}
                </InfoBox>
              </Grid>
              <Grid item xs={12} md>
                <InfoBox
                  type="warning"
                  title={t(translations.pages.profile.summaryInfoBlockedDepartments.title())}
                >
                  {t(translations.pages.profile.summaryInfoBlockedDepartments.content())}
                </InfoBox>
              </Grid>
            </Grid>
            <Grid container spacing={2} className="shibUserDetail__summary__settings">
              <Grid item xs="auto" className="shibUserDetail__summary__settings__switch">
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={emailsEnabled}
                      defaultChecked={settings.active}
                      onChange={ev => {
                        setEmailsEnabled(ev.target.checked);
                        handleSave({
                          active: ev.target.checked
                        });
                      }}
                    />
                  }
                  label={t(translations.pages.profile.receive()) as string}
                />
              </Grid>
              <Grid item xs md={6} lg={4}>
                <Slider
                  disabled={!emailsEnabled}
                  defaultValue={settings.interval}
                  valueLabelDisplay="auto"
                  step={1}
                  marks={sliderMarks}
                  min={1}
                  max={14}
                  onChange={(_event, newValue) => {
                    handleSave({
                      interval: newValue
                    });
                  }}
                />
              </Grid>
            </Grid>
          </>
        );
      }}
    </GQLGuard>
  );
}
